// App-wide dialogs: menus, confirmations and notices, rendered by <DialogHost /> once at the
// root. Same look on iOS, Android, web and Telegram (react-native-web's Alert is a no-op).
import React from 'react';
import { Pressable, Text, View } from 'react-native';
import { create } from 'zustand';
import Sheet from './Sheet';
import Icon, { type IconName } from './Icon';
import { Body, Button } from './ui';
import i18n from '../i18n';
import { font, radius, spacing, usePalette } from '../theme';

export type MenuItem = { label: string; icon?: IconName; destructive?: boolean; disabled?: boolean; onPress: () => void };

type Dialog =
  | { kind: 'menu'; title?: string; items: MenuItem[] }
  | { kind: 'confirm'; title: string; message?: string; confirmLabel: string; destructive: boolean; resolve: (ok: boolean) => void }
  | { kind: 'notice'; title?: string; message: string }
  | { kind: 'choose'; title: string; message?: string; options: { value: string; label: string }[]; resolve: (value: string | null) => void };

const useDialog = create<{ current: Dialog | null }>(() => ({ current: null }));
const close = () => useDialog.setState({ current: null });

export function showMenu(items: MenuItem[], title?: string): void {
  useDialog.setState({ current: { kind: 'menu', title, items: items.filter(Boolean) } });
}

export function confirm(title: string, message?: string, confirmLabel = i18n.t('common.delete'), destructive = true): Promise<boolean> {
  return new Promise(resolve => useDialog.setState({ current: { kind: 'confirm', title, message, confirmLabel, destructive, resolve } }));
}

/** One of several answers, or null when dismissed. */
export function choose<T extends string>(title: string, message: string | undefined, options: { value: T; label: string }[]): Promise<T | null> {
  return new Promise(resolve => useDialog.setState({ current: { kind: 'choose', title, message, options, resolve: resolve as (v: string | null) => void } }));
}

export function notify(message: string, title?: string): void {
  useDialog.setState({ current: { kind: 'notice', title, message } });
}

export function DialogHost() {
  const current = useDialog(s => s.current);
  const p = usePalette();
  if (!current) return null;

  if (current.kind === 'menu') {
    return (
      <Sheet visible onClose={close} title={current.title}>
        <View style={{ backgroundColor: p.card, borderRadius: radius.l, overflow: 'hidden' }}>
          {current.items.map((item, i) => (
            <Pressable
              key={`${item.label}-${i}`}
              disabled={item.disabled}
              accessibilityRole="button"
              onPress={() => {
                close();
                item.onPress();
              }}
              style={({ pressed }) => ({
                flexDirection: 'row', alignItems: 'center', gap: spacing.m, paddingHorizontal: spacing.l, minHeight: 52,
                borderTopWidth: i ? 0.5 : 0, borderTopColor: p.border, opacity: item.disabled ? 0.35 : pressed ? 0.6 : 1,
              })}>
              {item.icon ? <Icon name={item.icon} color={item.destructive ? p.danger : p.text} /> : null}
              <Text style={{ fontSize: font.body, color: item.destructive ? p.danger : p.text }}>{item.label}</Text>
            </Pressable>
          ))}
        </View>
      </Sheet>
    );
  }

  if (current.kind === 'confirm') {
    const done = (ok: boolean) => {
      close();
      current.resolve(ok);
    };
    return (
      <Sheet visible onClose={() => done(false)} title={current.title}>
        {current.message ? <Body secondary>{current.message}</Body> : null}
        <Button label={current.confirmLabel} variant={current.destructive ? 'danger' : 'primary'} onPress={() => done(true)} />
        <Button label={i18n.t('common.cancel')} variant="secondary" onPress={() => done(false)} />
      </Sheet>
    );
  }

  if (current.kind === 'choose') {
    const done = (value: string | null) => {
      close();
      current.resolve(value);
    };
    return (
      <Sheet visible onClose={() => done(null)} title={current.title}>
        {current.message ? <Body secondary>{current.message}</Body> : null}
        {current.options.map((o, i) => <Button key={o.value} label={o.label} variant={i ? 'secondary' : 'primary'} onPress={() => done(o.value)} />)}
        <Button label={i18n.t('common.cancel')} variant="plain" onPress={() => done(null)} />
      </Sheet>
    );
  }

  return (
    <Sheet visible onClose={close} title={current.title}>
      <Body>{current.message}</Body>
      <Button label={i18n.t('common.ok')} onPress={close} />
    </Sheet>
  );
}
